import { Stack } from '@mui/material';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useConstants } from '../../../../hooks/useConstants';
import Select from '../../../forms/Select';

interface CategoryProps {
  disabled?: boolean;
}

export default function Category(props: CategoryProps) {
  const { constants } = useConstants();

  const ACCEPTED_CATEGORIES = get(constants, 'CATEGORY.ACCEPTED_CATEGORIES', []);
  const ACCEPTED_SUB_CATEGORIES = get(constants, 'CATEGORY.ACCEPTED_SUB_CATEGORIES', []);

  const { watch, resetField } = useFormContext();

  const [subcategoryList, setSubcategoryList] = useState([]);

  const watchCategory = watch('category');
  const watchSubcategory = watch('subcategory');

  const { disabled = false } = props;

  // On load, if there is no category selected, then auto select the first one.
  useEffect(() => {
    if (!isEmpty(ACCEPTED_CATEGORIES) && !isEmpty(ACCEPTED_SUB_CATEGORIES)) {
      // Whenever category is blank, then set subcategory to null
      // because there are no subcategory items in that blank category.
      if (watchCategory === '') {
        resetField('subcategory', { defaultValue: null });
        return;
      }

      let selectedCategory = watchCategory;
      if (isNil(selectedCategory)) {
        [[selectedCategory]] = ACCEPTED_CATEGORIES;
        resetField('category', { defaultValue: selectedCategory });
      }

      const acceptedSubcategories = [
        ['', 'None'],
        ...ACCEPTED_SUB_CATEGORIES[selectedCategory],
      ];

      setSubcategoryList(acceptedSubcategories);

      // If the original category was nil,
      // or the current subcategory does not exist in the list of subcategories
      // then set the default subcategory to be the first item.
      if (acceptedSubcategories) {
        if (isNil(watchCategory) || !acceptedSubcategories.map((item) => item[0]).includes(watchSubcategory)) {
          const defaultSubcategory = acceptedSubcategories[1][0];
          resetField('subcategory', { defaultValue: defaultSubcategory });
        }
      } else {
        setSubcategoryList([]);
      }

      if (watchCategory === '') {
        resetField('subcategory', { defaultValue: null });
      }
    }
  }, [watchCategory, resetField, ACCEPTED_CATEGORIES, ACCEPTED_SUB_CATEGORIES, watchSubcategory]);

  return (
    <Stack width={1} direction="row" spacing={2}>
      <Select
        sx={{ minWidth: '150px' }}
        label="Category"
        name="category"
        options={[{ value: '', label: 'None' }, ...ACCEPTED_CATEGORIES.map(([value, label]) => ({ value, label }))]}
        rules={{
          required: 'Please select a category.',
        }}
        disabled={disabled}
      />
      <Select
        sx={{ minWidth: '150px' }}
        label="Subcategory"
        name="subcategory"
        options={subcategoryList.map(([value, label]) => ({ value, label: `${label}${value && ` (${value})`}`}))}
        rules={{
          required: 'Please select a subcategory.',
        }}
        disabled={disabled}
      />
    </Stack>
  );
}
